<template>
  <div :style="mdAndDown ? 'overflow-y: auto; height: 80vh;' : ''">
    <v-row class="mb-2 d-flex align-center">
      <v-col
        cols="12"
        lg="9"
      >
        <div class="d-flex flex-column flex-lg-row align-stretch align-lg-center ga-2">
          <ActionMenu
            @click:action:change="handleUpdateProvider($event)"
            :button-block="$vuetify.display.mdAndDown"
            :button-title="
              providers.find((provider) => provider.id == providerId)?.name ||
              $bct('Select a location')
            "
            :items="providerActionItems"
            :no-translate="true"
            button-class="me-2 bg-super-light-grey px-3 py-2 rounded"
            button-color="black"
            button-icon-side="right"
            button-text
          />

          <ActionMenu
            v-if="providerId"
            @click:action:change="handleChangeProgram($event)"
            :button-block="$vuetify.display.mdAndDown"
            :button-title="selectedProgram?.name || `Select a ${terms.program.toLowerCase()}`"
            :items="programActionItems"
            button-class="me-2 bg-super-light-grey px-3 py-2 rounded"
            button-color="black"
            button-icon-side="right"
            button-text
          />

          <ActionMenu
            v-if="!subsidyProgram?.enable_attendance_v2 && selectedProgram"
            @click:action:change="changeOperatingLog($event)"
            :button-block="$vuetify.display.mdAndDown"
            :button-title="selectedMonth || 'Select a month'"
            :items="operatingLogActionItems"
            button-class="bg-super-light-grey px-3 py-2 rounded"
            button-color="black"
            button-icon-side="right"
            button-text
          />

          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
          <v-text-field
            v-if="selectedProgram && subsidyProgram?.enable_attendance_v2"
            v-model="childSearch"
            @click:clear="clearChildSearchFilter"
            @input="debouncedApplyChildSearch"
            :aria-label="$bct('Search by name or external ID')"
            :class="mdAndDown ? 'w-full' : 'mxw-350'"
            :placeholder="$bct('Search by name or external ID')"
            color="primary"
            density="compact"
            prepend-inner-icon="search"
            variant="solo-filled"
            clearable
            hide-details
          />
        </div>
      </v-col>
      <v-col
        v-if="selectedOperatingLog"
        :class="mdAndDown ? 'd-flex flex-column ga-2' : 'd-flex justify-end'"
        cols="12"
        lg="3"
      >
        <v-btn
          v-if="showViewAttendanceFormButton"
          @click="handleOpenOperatingLog(operatingLogEditor)"
          :block="$vuetify.display.mdAndDown"
          class="me-3"
          color="primary"
          variant="outlined"
        >
          {{ $bct('View attendance form') }}
        </v-btn>

        <v-btn
          v-if="shouldShowEditSelectedButton"
          @click="openBulkEditor"
          :block="mdAndDown"
          class="me-3"
          color="primary"
        >
          {{ $bct('Edit Selected') }}
        </v-btn>

        <template v-if="!subsidyProgram?.enable_individual_attendance_logs">
          <v-btn
            v-if="showOperatingLogButton"
            @click="handleOpenOperatingLog(operatingLogEditor)"
            :block="$vuetify.display.mdAndDown"
            :loading="processing"
            :variant="!!selectedOperatingLog.submitted_at ? 'outlined' : 'flat'"
            class="me-3"
            color="primary"
            data-testId="submit-attendance-log"
          >
            <template v-if="selectedOperatingLog.submitted_at">
              <v-icon
                class="me-2"
                icon="check"
              />
              <span>
                {{ $bct('Submitted') }}
              </span>
            </template>
            <template v-else>
              <span>
                {{ $bct('Submit') }}
              </span>
            </template>
          </v-btn>

          <v-tooltip
            v-if="showOperatingLogSubmitButtonV2"
            :disabled="!disabledV2SubmitButtonTooltip"
            location="bottom"
          >
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps">
                <StandardButton
                  @click="handleOpenOperatingLog(operatingLogEditor)"
                  :disabled="shouldDisableV2SubmitButton"
                  :full-width="mdAndDown"
                  :loading="processing"
                  :prepend-icon="selectedOperatingLog.submitted_at ? 'check' : ''"
                  :text="selectedOperatingLog.submitted_at ? $bct('Submitted') : $bct('Submit')"
                  :variant="!!selectedOperatingLog.submitted_at ? 'outlined' : 'flat'"
                  color="primary"
                  custom-classes="me-3"
                  data-testid="submit-attendance-log"
                />
              </span>
            </template>

            <span>
              {{ $bct(disabledV2SubmitButtonTooltip) }}
            </span>
          </v-tooltip>
        </template>

        <ActionMenu
          :items="operatingLogActions"
          button-icon="more_vert"
          label="Download Data"
        />
      </v-col>
    </v-row>

    <template v-if="selectedOperatingLog">
      <AttendanceLogEditorFilter
        @update-filter="handleUpdateFilters($event)"
        ref="attendanceLogEditorFilter"
        :current-submitted-filter="currentSubmittedFilter"
        :enable-attendance-v2="subsidyProgram?.enable_attendance_v2"
        :month-filter-selections="monthFilterSelections"
        :month-options="attendanceLogsMonthFilters"
        :sticky="mdAndDown"
      />

      <AttendanceLogEditor
        v-model:selected-attendance-logs="selectedAttendanceLogIds"
        @change:selected-attendance-log="loadAttendanceLogs"
        @queue:attendance-log-change="queueAttendanceLogChange($event)"
        @queue:attendance-log-question="queueAttendanceLogQuestion($event)"
        @queue:operating-log-change="queueOperatingLogChange($event)"
        @update:attendance-log-page="navigateAttendanceLogsPageTo($event)"
        ref="attendanceLogEditor"
        :attendance-log-questions="attendanceLogQuestions"
        :attendance-log-schema="attendanceLogSchema"
        :attendance-logs="attendanceLogs"
        :attendance-totals="attendanceTotals"
        :current-page="attendanceLogsCurrentPage"
        :daily-totals="dailyTotals"
        :enable-attendance-v2="subsidyProgram?.enable_attendance_v2"
        :enable-individual-attendance="subsidyProgram?.enable_individual_attendance_logs"
        :errors="errors"
        :operating-log="selectedOperatingLog"
        :operating-log-schema="operatingLogSchema"
        :page-size="attendanceLogsPageSize"
        :queued-attendance-log-answers="queuedAttendanceLogAnswers"
        :queued-attendance-log-changes="queuedAttendanceLogChanges"
        :queued-attendance-log-question-id="queuedAttendanceLogQuestionId"
        :queued-operating-log-changes="queuedOperatingLogChanges"
        :read-only="isAttendanceLogReadOnly"
        :submit-revision="selectedOperatingLog?.submission_revision"
        :subsidy-program="subsidyProgram"
        :total-items-count="attendanceLogsTotalItemsCount"
        :total-pages="attendanceLogsTotalPages"
      />
    </template>

    <template v-else>
      <NullState hide-new-button />
    </template>

    <AttendanceLogQuestionDialog
      @close="clearAttendanceLogQuestionQueue"
      @save="updateAttendanceLogsAnswers"
      ref="attendanceLogQuestionDialog"
      :processing="processing"
      :schema-definition="attendanceLogSchema?.definition"
    />

    <ResourceDialog
      @close="clearAttendanceChangeQueue"
      ref="attendanceLogStatusDialog"
      :processing="processing"
      :title="`Set ${terms.attendance.toLowerCase()} status`"
      closeable
      hide-actions
    >
      <template #form>
        <v-btn
          v-for="status in attendanceStatuses"
          @click="updateAttendanceLogsStatus(status)"
          :key="status.code"
          :color="status.color"
          :loading="processing"
          class="mb-2 me-2 px-4"
          size="x-large"
          variant="flat"
          block
          tile
        >
          {{ $bct(status.code) }} - {{ $bct(status.title) }}
        </v-btn>
      </template>
    </ResourceDialog>

    <ResourceDialog
      @save="updateAttendanceLogsHours"
      ref="attendanceLogHoursDialog"
      :fields="ATTENDANCE_LOG_HOURS_FIELDS"
      :processing="processing"
      title="Set hours attended"
      closeable
    />

    <ResourceDialog
      @close="clearOperatingLogChangeQueue"
      ref="operatingLogStatusDialog"
      :processing="processing"
      title="Set operating status"
      closeable
      hide-actions
    >
      <template #form>
        <v-btn
          v-for="status in operatingStatuses"
          @click="updateOperatingLog(status)"
          :key="status.code"
          :color="status.color"
          :loading="processing"
          class="mb-2 me-2 px-4"
          size="x-large"
          variant="flat"
          block
          tile
        >
          {{ $bct(status.code) }} - {{ $bct(status.title) }}
        </v-btn>
      </template>
    </ResourceDialog>

    <Teleport
      :disabled="!teleportTargetExists"
      to="#attendance-log-editor-bar"
      defer
    >
      <OperatingLogEditor
        @change="handleOperatingLogEditorChange"
        ref="operatingLogEditor"
        :attendance-log-questions="attendanceLogQuestions"
        :attendance-logs="attendanceLogs"
        :schema-id="operatingLogSchema?.id"
        :subsidy-program="subsidyProgram"
      />
    </Teleport>
    <ChangesQueuedAlert
      v-if="attendanceLogSchema"
      @clear:attendance-log-changes="clearAttendanceChangeQueue"
      @clear:attendance-log-question="clearAttendanceLogQuestionQueue"
      @clear:attendance-log-status="clearAttendanceLog"
      @clear:operating-log-changes="clearOperatingLogChangeQueue"
      @clear:operating-log-status="clearOperatingLog"
      @edit:attendance-log-question="openAttendanceLogQuestionDialog"
      @edit:attendance-log-status="handleEditAttendanceLogStatus"
      @edit:operating-log="operatingLogStatusDialog.open()"
      :attendance-log-schema="attendanceLogSchema"
      :queued-attendance-log-answers="queuedAttendanceLogAnswers"
      :queued-attendance-log-changes="queuedAttendanceLogChanges"
      :queued-operating-log-changes="queuedOperatingLogChanges"
      :show-attendance-log-change-queue="showAttendanceLogChangeQueue"
      :show-attendance-log-question-queue="showAttendanceLogQuestionQueue"
      :show-operating-log-change-queue="showOperatingLogChangeQueue"
    />
  </div>
</template>

<script setup>
import _ from 'lodash';
import { onMounted, toRef } from 'vue';
import { useDisplay } from 'vuetify';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import AttendanceLogEditor from '@/shared/components/attendance-logs/AttendanceLogEditor.vue';
import AttendanceLogEditorFilter from '@/shared/components/attendance-logs/AttendanceLogEditorFilter.vue';
import AttendanceLogQuestionDialog from '@/shared/components/attendance-logs/AttendanceLogQuestionDialog.vue';
import ChangesQueuedAlert from '@/shared/components/ChangesQueuedAlert.vue';
import NullState from '@/shared/components/NullState.vue';
import OperatingLogEditor from '@/shared/components/OperatingLogEditor.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useAttendance from '@/shared/composables/useAttendance';
import useTerms from '@/shared/composables/useTerms';

const { terms } = useTerms();
const { mdAndDown } = useDisplay();

const $bct = inject('translator');

const props = defineProps({
  subsidyProgramId: {
    type: String,
    default: null,
  },
  subsidyPrograms: {
    type: Array,
    default: () => [],
  },
});

const attendanceLogEditor = ref(null);
const attendanceLogEditorFilter = ref(null);
const childSearch = ref('');
const operatingLogEditor = ref(null);
const selectedAttendanceLogIds = ref([]);
const subsidyProgram = computed(() => {
  return props.subsidyPrograms.find((sp) => sp.id === props.subsidyProgramId);
});

const teleportTargetExists = ref(false);

onMounted(() => {
  teleportTargetExists.value = document.getElementById('attendance-log-editor-bar') !== null;
});

const hasSelectedAttendanceLogs = computed(() => {
  return attendanceLogEditor.value?.hasAttendanceLogsSelected?.() ?? false;
});

const {
  ATTENDANCE_LOG_HOURS_FIELDS,
  allAttendanceLogsComplete,
  allAttendanceLogsReadyForSubmission,
  attendanceLink,
  attendanceStatuses,
  attendanceLogFilters,
  // these refs are only used in the template so the linter thinks they're unused
  attendanceLogHoursDialog,
  attendanceLogStatusDialog,
  attendanceLogQuestionDialog,
  //
  operatingLogStatusDialog,
  attendanceLogs,
  attendanceLogSchema,
  attendanceLogQuestions,
  attendanceTotals,
  attendanceLogsCurrentPage,
  attendanceLogsPageSize,
  attendanceLogsTotalPages,
  attendanceLogsMonthFilters,
  attendanceLogsTotalItemsCount,
  currentSubmittedFilter,
  errors,
  handleEditAttendanceLogStatus,
  handleOpenOperatingLog,
  operatingLogActionItems,
  operatingLogLink,
  operatingStatuses,
  processing,
  programActionItems,
  providerId,
  operatingLogSchema,
  queuedAttendanceLogQuestionId,
  queuedAttendanceLogAnswers,
  queuedOperatingLogChanges,
  providers,
  queuedAttendanceLogChanges,
  selectedOperatingLog,
  selectedProgram,
  showAttendanceLogChangeQueue,
  showAttendanceLogQuestionQueue,
  showOperatingLogChangeQueue,
  dailyTotals,
  selectedMonth,
  changeOperatingLog,
  changeProgram,
  changeProvider,
  clearAttendanceLog,
  clearOperatingLog,
  clearAttendanceChangeQueue,
  clearOperatingLogChangeQueue,
  clearAttendanceLogQuestionQueue,
  loadAttendanceLogs,
  loadAttendanceLogQuestions,
  loadProviders,
  openAttendanceLogQuestionDialog,
  queueAttendanceLogChange,
  queueAttendanceLogQuestion,
  queueOperatingLogChange,
  setAttendanceLogFilters,
  updateOperatingLog,
  updateAttendanceLogsAnswers,
  updateAttendanceLogsStatus,
  updateAttendanceLogsHours,
  navigateAttendanceLogsPageTo,
  monthFilterSelections,
} = useAttendance(toRef(props, 'subsidyProgramId'));

const showViewAttendanceFormButton = computed(() => {
  if (!subsidyProgram.value?.enable_attendance_v2) return false;
  if (subsidyProgram.value?.enable_individual_attendance_logs) return false;
  if (selectedOperatingLog.value?.submitted_at) return false;
  if (processing.value) return false;

  const monthFilters = attendanceLogFilters.value?.['filters[month_year]'];
  if (monthFilters?.length !== 1) return false;

  if (allAttendanceLogsComplete.value) return false;

  return true;
});

const showOperatingLogButton = computed(() => {
  return (
    !subsidyProgram.value?.enable_individual_attendance_logs &&
    !subsidyProgram.value?.enable_attendance_v2
  );
});

const isFilteredToSingleMonth = computed(() => {
  return attendanceLogEditorFilter?.value?.isFilteredToSingleMonth;
});

const disabledV2SubmitButtonTooltip = computed(() => {
  if (!isFilteredToSingleMonth.value) return 'Filter to a single month to submit attendance.';
  if (!allAttendanceLogsReadyForSubmission.value)
    return 'All attendance logs must be complete to submit attendance.';
  return null;
});

const shouldDisableV2SubmitButton = computed(() => {
  return !isFilteredToSingleMonth.value || !allAttendanceLogsReadyForSubmission.value;
});

const showOperatingLogSubmitButtonV2 = computed(() => {
  return (
    subsidyProgram.value?.enable_attendance_v2 &&
    !subsidyProgram.value?.enable_individual_attendance_logs
  );
});

const operatingLogActions = computed(() => {
  if (selectedOperatingLog.value) {
    return [
      { href: attendanceLink.value, title: `Download ${terms.value.attendance}` },
      { href: operatingLogLink.value, title: 'Download Form' },
    ];
  }
  return [];
});

const providerActionItems = computed(() => {
  return providers.value.map((provider) => ({
    event: 'change',
    title: provider.name,
    providerId: provider.id,
  }));
});

const isAttendanceLogReadOnly = computed(() => {
  return (
    subsidyProgram.value?.enable_daily_attendance &&
    subsidyProgram.value?.enable_attendance_v2 &&
    monthFilterSelections.value.length !== 1
  );
});

const shouldShowEditSelectedButton = computed(() => {
  return subsidyProgram.value?.enable_bulk_editing_attendance && hasSelectedAttendanceLogs.value;
});

function openBulkEditor() {
  attendanceLogEditor.value?.openBulkEditor?.();
}

async function handleUpdateFilters(event) {
  if (event.key === 'child_search' && !event.value) {
    event.value = '';
  }
  attendanceLogEditor.value?.clearSelection?.();
  setAttendanceLogFilters(event);
}

const debouncedApplyChildSearch = _.debounce(() => {
  void handleUpdateFilters({ key: 'child_search', value: childSearch.value ?? '' });
}, 500);

function clearChildSearchFilter() {
  debouncedApplyChildSearch.cancel();
  void handleUpdateFilters({ key: 'child_search', value: '' });
}

onBeforeUnmount(() => {
  debouncedApplyChildSearch.cancel();
});

async function handleChangeProgram(event) {
  childSearch.value = '';
  debouncedApplyChildSearch.cancel();
  attendanceLogEditor.value?.clearSelection?.();
  changeProgram(event);
}

async function handleUpdateProvider(event) {
  childSearch.value = '';
  debouncedApplyChildSearch.cancel();
  attendanceLogEditor.value?.clearSelection?.();
  changeProvider(event);
}

function handleOperatingLogEditorChange(event) {
  childSearch.value = '';
  debouncedApplyChildSearch.cancel();
  selectedOperatingLog.value = event;
  attendanceLogEditor.value?.clearSelection?.();
  void loadAttendanceLogs();
}

watch(
  () => props.subsidyProgramId,
  async () => {
    attendanceLogEditor.value?.clearSelection?.();
    providerId.value = null;
    selectedProgram.value = null;
    selectedOperatingLog.value = null;
    await loadProviders();
  },
);

onMounted(async () => {
  await loadProviders();
  await loadAttendanceLogQuestions();

  if (selectedProgram.value) {
    await setAttendanceLogFilters({ key: 'submitted', value: ['Unsubmitted'] });
  }
});
</script>
